// The homecoming plan: the last days before pick-up, what to ask the
// breeder, pick-up day hour by hour, the first night, and days 1–7.
// Written for a cocker puppy of 8–9 weeks coming home to a city flat with
// no garden. Based on Dogs Trust, RSPCA, Blue Cross, PDSA and Royal Kennel
// Club puppy advice, and the Highway Code (rule 57) — see docs/RESEARCH.md.

export interface PlanItem {
  id: string;
  text: string;
  detail?: string;
  /** In-app link for more. */
  route?: string;
}

export interface PlanSection {
  id: string;
  /** Days relative to pick-up day (0). Prep is negative; the first night is 0 in the evening. */
  day: number;
  /** For day 0: which part of the day. */
  part?: 'day' | 'night';
  title: string;
  intro: string;
  items: PlanItem[];
}

export const HOMECOMING: PlanSection[] = [
  {
    id: 'prep',
    day: -3,
    title: 'Before pick-up day',
    intro: 'Get the flat ready so the first days are calm and boring — in the best way.',
    items: [
      { id: 'prep-crate', text: 'Set up his crate in a quiet corner of the living room', detail: 'Soft bedding, a cover over three sides, door propped open. Put a second bed (or the crate) beside your bed for the first nights.', route: '/guide/sleep-and-crate' },
      { id: 'prep-zone', text: 'Make a puppy-safe zone with a pen or baby gate', detail: 'Somewhere he can be when you can’t watch him — near the door to make toilet trips quick.' },
      { id: 'prep-proof', text: 'Puppy-proof the flat', detail: 'Cables, phone chargers, shoes, bins, cleaning products, houseplants, anything at nose height. Check the balcony and the gap under the front door.' },
      { id: 'prep-food', text: 'Buy the breeder’s food — exactly the same one', detail: 'No food changes for the first two weeks. Switch gradually later if you want to.', route: '/food' },
      { id: 'prep-clean', text: 'Enzyme cleaner, kitchen roll and poo bags by the door', detail: 'Normal cleaners leave a scent that invites him back to the same spot.' },
      { id: 'prep-tag', text: 'Collar and ID tag with your name and address', detail: 'The law requires it in public (Control of Dogs Order 1992). He won’t walk out yet, but get him used to the collar.' },
      { id: 'prep-vet', text: 'Book a vet check for day 2 or 3', detail: 'Tell them he’s a new puppy — many practices do a free first check.', route: '/health' },
      { id: 'prep-insurance', text: 'Sort insurance from day one', detail: 'Activate any free cover from the breeder, then choose a lifetime policy before it runs out.' },
      { id: 'prep-rules', text: 'Agree house rules and cue words together', detail: 'Sofa? Bedroom? Who does which night? Same words, every time.', route: '/training/rules' },
      { id: 'prep-blanket', text: 'Send a blanket to the breeder to pick up mum’s scent', detail: 'Or ask them to put one in with the litter for a few days — it helps enormously on the first night.' },
      { id: 'prep-car', text: 'Pack the car kit', detail: 'A small crate or carrier you can secure with a seat belt, towels, kitchen roll, poo bags, water and a bowl. Dogs must be suitably restrained in a car (Highway Code rule 57).' },
      { id: 'prep-nights', text: 'Decide who’s on duty for the first nights', detail: 'Take turns — whoever is on sleeps near him and does the night toilet trip.' },
    ],
  },
  {
    id: 'breeder',
    day: 0,
    part: 'day',
    title: 'What to ask the breeder',
    intro: 'Go through this before you leave — it’s easy to forget with a puppy on your lap.',
    items: [
      { id: 'breeder-routine', text: 'His routine: meal times, amounts and the exact food', detail: 'Write down how much per meal and when. Copy their rhythm at first.' },
      { id: 'breeder-worm', text: 'Worming and flea treatments so far — product and dates', route: '/health' },
      { id: 'breeder-vacc', text: 'Vaccination card, if he’s had his first jab' },
      { id: 'breeder-chip', text: 'Microchip number and the database it’s on', detail: 'Breeders must chip before 8 weeks. You must update the details into your names.' },
      { id: 'breeder-kc', text: 'Kennel Club registration papers (if registered)' },
      { id: 'breeder-health', text: 'Parents’ health test results', detail: 'For cockers: eye tests and DNA tests (e.g. PRA, FN) and hip scores.' },
      { id: 'breeder-contract', text: 'Puppy contract and insurance cover note' },
      { id: 'breeder-scent', text: 'His scent blanket or a toy from the litter' },
      { id: 'breeder-mum', text: 'Watch him with mum and littermates for a few minutes', detail: 'How he plays and settles tells you a lot.' },
    ],
  },
  {
    id: 'pickup',
    day: 0,
    part: 'day',
    title: 'Pick-up day',
    intro: 'Keep it quiet and simple. Today is about him feeling safe, not about showing him off.',
    items: [
      { id: 'pickup-fast', text: 'Small or no breakfast for him about two hours before the drive', detail: 'Travelling on a nearly empty tummy means less chance of car sickness. The breeder can help time this.' },
      { id: 'pickup-wee', text: 'Let him wee at the breeder’s before you set off' },
      { id: 'pickup-drive', text: 'The drive: crate secured, one of you sitting beside him', detail: 'Towels for accidents. Stop about every hour. Don’t put him down at service stations — he isn’t vaccinated yet.' },
      { id: 'pickup-spot', text: 'Arriving home: carry him straight to his toilet spot first', detail: 'Before you go up to the flat. Wait quietly, say the cue, and reward the moment he goes.', route: '/guide/toilet-training' },
      { id: 'pickup-explore', text: 'One quiet room, let him explore, crate door open', detail: 'Toss treats into the crate so it becomes the best place. No visitors today.' },
      { id: 'pickup-meal', text: 'A small meal about an hour after arriving' },
      { id: 'pickup-toilet', text: 'Toilet trip after waking, eating and playing — and every 45–60 minutes awake', detail: 'Log them — the app learns his rhythm and reminds you.' },
      { id: 'pickup-sleep', text: 'Let him sleep as much as he wants', detail: 'Puppies this age sleep 18–20 hours a day. A tired, overwhelmed puppy bites and cries more.' },
      { id: 'pickup-evening', text: 'Last meal about two hours before bed, then calm play', detail: 'A gentle game, a chew — nothing wild in the last hour.' },
    ],
  },
  {
    id: 'night1',
    day: 0,
    part: 'night',
    title: 'The first night',
    intro: 'The first night away from mum is the hardest. Being close is the kindest thing you can do.',
    items: [
      { id: 'night-close', text: 'Crate right beside your bed (or you sleep beside it)', detail: 'Being near you helps him settle. Move it gradually to where it will live later.', route: '/guide/sleep-and-crate' },
      { id: 'night-comfort', text: 'Scent blanket, a warm (not hot) wrapped water bottle, dim lights', detail: 'Soft white noise or a ticking clock can help too.' },
      { id: 'night-last', text: 'Last toilet trip right before bed' },
      { id: 'night-cry', text: 'When he cries: calm hand by the crate — he isn’t being naughty', detail: 'Don’t leave him to cry it out. Speak softly, a finger through the door.' },
      { id: 'night-trip', text: 'Expect 1–2 night toilet trips — boring and quick', detail: 'Carry him down, cue word, praise, straight back to bed. No play, no chat, lights low.' },
      { id: 'night-early', text: 'Early start: out to his spot the moment he wakes' },
    ],
  },
  {
    id: 'day1',
    day: 1,
    title: 'Day 1 — settling in',
    intro: 'Build the rhythm: toilet → food → play → toilet → sleep. Repeat.',
    items: [
      { id: 'd1-rhythm', text: 'Follow the rhythm on the Today screen', detail: 'Tap the big buttons as things happen — the reminders get smarter each day.' },
      { id: 'd1-naps', text: 'Naps in the crate with the door open, then closed for short spells' },
      { id: 'd1-name', text: 'Name game: say “Arlo”, reward when he looks', route: '/training/name-game' },
      { id: 'd1-handle', text: 'Gentle handling: ears, paws, mouth — seconds at a time with a treat', route: '/training/handling' },
    ],
  },
  {
    id: 'day2',
    day: 2,
    title: 'Day 2 — vet check',
    intro: 'A first health check, and the paperwork done.',
    items: [
      { id: 'd2-vet', text: 'Vet health check — carry him, keep him on your lap in the waiting room', detail: 'Take the breeder’s paperwork. Ask about the vaccination plan, worming and flea products (including lungworm), and when he can go out.', route: '/health' },
      { id: 'd2-chip', text: 'Update the microchip into your names', detail: 'A legal requirement — use the database the breeder gave you.' },
      { id: 'd2-weigh', text: 'Log his first weight', route: '/health/weight' },
      { id: 'd2-insure', text: 'Insurance in place' },
    ],
  },
  {
    id: 'day3',
    day: 3,
    title: 'Day 3 — crate love',
    intro: 'Make the crate the best place in the flat.',
    items: [
      { id: 'd3-crate', text: 'Crate games: treats inside, door open, walk away', route: '/training/crate-love' },
      { id: 'd3-alone', text: 'Tiny separations: step into the next room for a few seconds', detail: 'Come back before he minds. This is the start of alone-time training.', route: '/training/alone-time' },
      { id: 'd3-kong', text: 'Try a stuffed Kong or lick mat during a crate spell' },
    ],
  },
  {
    id: 'day4',
    day: 4,
    title: 'Day 4 — the world from your arms',
    intro: 'Before his jabs he can’t walk in public — but he can watch the world while you carry him.',
    items: [
      { id: 'd4-carry', text: 'Carry him for 5 minutes near a busy street or on a bench', detail: 'Buses, people, bikes. Treat when he watches calmly. Head home if he’s worried.', route: '/social' },
      { id: 'd4-sounds', text: 'Everyday sounds at home at low volume', detail: 'Hoover, hairdryer, doorbell, traffic. Pair each with treats.' },
      { id: 'd4-log', text: 'Tick off the new things in Socialise', route: '/social' },
    ],
  },
  {
    id: 'day5',
    day: 5,
    title: 'Day 5 — collar, lead and the lift',
    intro: 'Little introductions to things he’ll use for life.',
    items: [
      { id: 'd5-collar', text: 'Collar or harness on for a minute during a meal or game' },
      { id: 'd5-lead', text: 'Clip the lead on at home and let it trail (supervised)' },
      { id: 'd5-lift', text: 'Carry him in the lift or on the stairs, with treats', detail: 'Doors, echoes and neighbours — calm, short visits.' },
    ],
  },
  {
    id: 'day6',
    day: 6,
    title: 'Day 6 — a calm visitor',
    intro: 'One friendly adult, sitting on the floor, letting him come to them.',
    items: [
      { id: 'd6-visitor', text: 'One calm visitor for 20 minutes', detail: 'They sit, toss treats, and let him choose to say hello.' },
      { id: 'd6-bite', text: 'Practise “gentle”: play stops when teeth touch skin', route: '/training/gentle' },
      { id: 'd6-groom', text: 'First brush — a few strokes with treats', detail: 'Cocker coats need daily care later; start with 30 seconds.' },
    ],
  },
  {
    id: 'day7',
    day: 7,
    title: 'Day 7 — look back, plan week two',
    intro: 'A whole week. Look at what the logs say and adjust.',
    items: [
      { id: 'd7-insights', text: 'Check his toilet rhythm in Insights and adjust the routine', route: '/insights' },
      { id: 'd7-weigh', text: 'Weigh him again', route: '/health/weight' },
      { id: 'd7-photo', text: 'Take his weekly portrait', route: '/diary' },
      { id: 'd7-plan', text: 'Book puppy classes and plan the second vaccination', route: '/health' },
    ],
  },
];

export const ALL_PLAN_ITEMS = HOMECOMING.flatMap((s) => s.items.map((i) => ({ ...i, section: s.id })));
